import React from 'react'
import {
  AutocompleteOptions,
  AutocompleteState,
  createAutocomplete,
} from '@algolia/autocomplete-core'
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia'
import algoliasearch from 'algoliasearch/lite'
import { Hit } from '@algolia/client-search'
import ResourceType from './resource-type'
import ResourceBadge from './resource-badge'
import { Link } from 'gatsby'

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
)

// interface Categories {
//   name?: string
// }

type AutocompleteItem = Hit<{
  objectID: string
  date: string
  categories?: any[]
  modified: string
  slug: string
  title: string
  nodeType: string
  excerpt?: string
  uri: string
}>

export function Search(props: Partial<AutocompleteOptions<AutocompleteItem>>): JSX.Element {
  const [autocompleteState, setAutocompleteState] = React.useState<
    AutocompleteState<AutocompleteItem>
  >({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: '',
    activeItemId: null,
    status: 'idle',
  })

  const autocomplete = React.useMemo(
    () =>
      createAutocomplete<
        AutocompleteItem,
        React.BaseSyntheticEvent,
        React.MouseEvent,
        React.KeyboardEvent
      >({
        onStateChange({ state }) {
          setAutocompleteState(state)
        },
        getSources() {
          return [
            {
              sourceId: 'contents',
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: process.env.GATSBY_ALGOLIA_INDEX_NAME,
                      query,
                      params: {
                        hitsPerPage: 6,
                        highlightPreTag: '<mark>',
                        highlightPostTag: '</mark>',
                      },
                    },
                  ],
                })
              },
              getItemUrl({ item }) {
                return item.uri
              },
            },
          ]
        },
        ...props,
      }),
    [props]
  )

  const inputRef = React.useRef<HTMLInputElement>(null)
  const formRef = React.useRef<HTMLFormElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const { getEnvironmentProps } = autocomplete

  React.useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined
    }

    const { onTouchStart, onTouchMove } = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    })

    window.addEventListener('touchstart', onTouchStart)
    window.addEventListener('touchmove', onTouchMove)

    return () => {
      window.removeEventListener('touchstart', onTouchStart)
      window.removeEventListener('touchmove', onTouchMove)
    }
  }, [getEnvironmentProps, formRef, inputRef, panelRef])

  return (
    <div
      className='mw-search relative text-left w-full md:max-w-lg lg:max-w-xl xl:max-w-3xl'
      {...autocomplete.getRootProps({})}
    >
      <form
        ref={formRef}
        className={`flex items-center border focus-within:border-blue focus:border-blue hover:border-blue ${
          autocompleteState.isOpen ? 'rounded-t-xl border-blue' : 'rounded-xl border-gray-silver'
        } m-0 px-2`}
        {...autocomplete.getFormProps({ inputElement: inputRef.current })}
      >
        <div className='flex items-center order-1'>
          <label className='text-gray' {...autocomplete.getLabelProps({})}>
            <button className='p-2' type='submit' title='Submit'>
              <svg
                className='cursor-pointer text-gray-silver hover:text-blue-sky'
                xmlns='http://www.w3.org/2000/svg'
                width='14.997'
                height='15'
                viewBox='0 0 14.997 15'
              >
                <path
                  d='M14.793,12.968l-2.921-2.921a.7.7,0,0,0-.5-.205H10.9A6.09,6.09,0,1,0,9.843,10.9v.477a.7.7,0,0,0,.205.5l2.921,2.921a.7.7,0,0,0,.993,0l.829-.829A.706.706,0,0,0,14.793,12.968Zm-8.7-3.126a3.75,3.75,0,1,1,3.75-3.75A3.747,3.747,0,0,1,6.093,9.843Z'
                  fill='currentColor'
                />
              </svg>
            </button>
          </label>
        </div>
        <div className='order-3 relative w-full'>
          <input
            className='border-0 appearance-none background-none px-1 py-2 leading-relaxed tracking-wide body-2 caret-gray-silver text-gray'
            ref={inputRef}
            {...autocomplete.getInputProps({ inputElement: inputRef.current })}
          />
        </div>
        <div className='order-4'>
          <button
            className='hidden sm:flex items-center border-0 pointer h-full m-0 body-5 text-gray-silver hover:text-blue-sky px-2'
            title='Clear Search'
            type='reset'
          >
            Clear
          </button>
        </div>
      </form>

      {autocompleteState.isOpen && (
        <div
          ref={panelRef}
          className={[
            'overflow-hidden',
            'md:max-w-lg',
            'lg:max-w-xl xl:max-w-3xl',
            'w-full',
            '-left-1/2',
            'md:left-auto',
            'ml-50pct',
            'md:ml-0',
            '-mt-px',
            'shadow-md',
            'bg-white',
            'absolute',
            'transition-opacity',
            'duration-200',
            'border',
            'border-blue',
            'border-t-silver-gray',
            'rounded-none',
            autocompleteState.status === 'stalled' && 'opacity-70',
          ]
            .filter(Boolean)
            .join(' ')}
          {...autocomplete.getPanelProps({})}
        >
          <div className='mw-panel relative h-full m-0 p-2 text-left overflow-x-hidden overflow-y-auto'>
            {autocompleteState.collections.map((collection, index) => {
              const { source, items } = collection

              return (
                <section key={`source-${index}`} className='relative p-0 m-0 w-full'>
                  {items.length > 0 && (
                    <ul className='mw-list flex flex-col m-0 p-0' {...autocomplete.getListProps()}>
                      {items.map((item) => {
                        return (
                          <li
                            key={item.objectID}
                            className='mw-search__item m-0 rounded-md hover:bg-blue-tint overflow-hidden'
                            {...autocomplete.getItemProps({ item, source })}
                          >
                            <Link className='flex items-center p-2 md:p-4' to={item.uri}>
                              <ResourceBadge
                                className='hidden lg:block w-110'
                                resource={item.nodeType}
                              >
                                <ResourceType
                                  className='whitespace-nowrap'
                                  resource={item.nodeType}
                                />
                              </ResourceBadge>
                              <div className='grid ml-4 h-12'>
                                <div
                                  className='search-title text-gray body-2 truncate'
                                  dangerouslySetInnerHTML={{
                                    __html: item?._highlightResult!.title!?.value,
                                  }}
                                />
                                <div
                                  className='search-excerpt body-3 text-truncate'
                                  dangerouslySetInnerHTML={{
                                    __html: item?._highlightResult!.excerpt!?.value,
                                  }}
                                />
                              </div>
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </section>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
